import { supabase } from './supabase.js';
import { coerceUrl } from './url.js';

const SELECT_COLUMNS = [
  'id',
  'name',
  'type',
  'description',
  'url',
  'institution',
  'status',
  'submitted_by',
  'created_at',
  'updated_at',
].join(', ');

export const RESEARCH_TOOL_TYPES = ['archive', 'digital_library', 'online_edition', 'database', 'other'];

export const EMPTY_RESEARCH_TOOL = {
  name: '',
  type: 'archive',
  description: '',
  url: '',
  institution: '',
};

export const EMPTY_RESEARCH_TOOL_FILTERS = {
  type: '',
};

export async function fetchPublishedResearchTools() {
  const { data, error } = await supabase
    .from('research_tools')
    .select(SELECT_COLUMNS)
    .eq('status', 'published')
    .order('name', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function fetchResearchTool(id) {
  const { data, error } = await supabase
    .from('research_tools')
    .select(SELECT_COLUMNS)
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

function normalize(form) {
  const blank = (v) => (v == null || v === '' ? null : v);
  return {
    name: form.name?.trim(),
    type: form.type,
    description: blank(form.description),
    url: coerceUrl(form.url),
    institution: blank(form.institution?.trim?.()),
  };
}

export function validateResearchTool(form) {
  if (!form.name?.trim()) return 'researchTools.form.errors.nameRequired';
  if (!RESEARCH_TOOL_TYPES.includes(form.type)) return 'researchTools.form.errors.typeRequired';
  if (!form.url?.trim()) return 'researchTools.form.errors.urlRequired';
  return null;
}

// announce_in_whats_new is only written on create; see migration 019/020.
export async function createResearchTool(form, { authorId, asAdmin }) {
  const row = {
    ...normalize(form),
    announce_in_whats_new: Boolean(form.announce_in_whats_new),
    status: asAdmin ? 'published' : 'pending',
    submitted_by: authorId,
  };
  const { data, error } = await supabase
    .from('research_tools')
    .insert(row)
    .select(SELECT_COLUMNS)
    .single();
  if (error) throw error;
  return data;
}

export async function updateResearchTool(id, form) {
  const { data, error } = await supabase
    .from('research_tools')
    .update(normalize(form))
    .eq('id', id)
    .select(SELECT_COLUMNS)
    .single();
  if (error) throw error;
  return data;
}

export async function deleteResearchTool(id) {
  const { error } = await supabase.from('research_tools').delete().eq('id', id);
  if (error) throw error;
}

export function filterResearchTools(items, { type, query }) {
  const q = (query ?? '').trim().toLowerCase();
  return items.filter((r) => {
    if (type && r.type !== type) return false;
    if (q) {
      const haystack = [r.name, r.institution, r.description]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

export function deriveResearchToolFacets(items) {
  const types = new Map();
  for (const r of items) {
    if (r.type) types.set(r.type, (types.get(r.type) ?? 0) + 1);
  }
  return {
    types: RESEARCH_TOOL_TYPES.map((t) => ({ type: t, count: types.get(t) ?? 0 })),
  };
}
